import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../flags';

interface TermsOfServiceDialogProps {
  open: boolean;
  onClose?: () => void;
  onAccept?: () => void;
  onDecline?: () => void;
  showActions?: boolean;
}

export const TermsOfServiceDialog: React.FC<TermsOfServiceDialogProps> = ({
  open,
  onClose,
  onAccept,
  onDecline,
  showActions = false,
}) => {
  const { hasFeature } = useFeatureFlags();
  const showAICopy = hasFeature(FeatureFlags.NGW_AI_IMAGE_DESCRIPTIONS);
  const acceptCopy = showAICopy
    ? 'By clicking "Accept," you acknowledge and agree to the following terms regarding the use of media content and AI-generated descriptions on Bright MLS.'
    : 'By clicking "Accept," you acknowledge and agree to the following terms regarding the use of media content on Bright MLS.';

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      open={open}
      onClose={onClose}
      aria-labelledby="tos-dialog-title"
    >
      <DialogTitle
        id="tos-dialog-title"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 'h3.fontSize',
          fontWeight: 'h3.fontWeight',
        }}
      >
        Media Center Notice
        {onClose && (
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ fontWeight: 'bold', mt: 1 }}>
          Media Center Consent
        </DialogContentText>

        <DialogContentText sx={{ mb: 3 }}>
          Subscribers and the MLS may be sued if proper consent for use of
          Images and Documents is not obtained (including photographs,
          documents, drawings, graphics, renderings, blueprints, audio files,
          videos, etc.) You confirm that you either own the copyright(s) or have
          obtained the necessary written consent for each Image and Document
          uploaded, in compliance with the Subscription Agreement.
        </DialogContentText>

        {showAICopy && (
          <Box>
            <DialogContentText sx={{ fontWeight: 'bold' }}>
              AI Generated Image Description
            </DialogContentText>

            <DialogContentText>
              This artificial intelligence (AI) tool is intended to aid, but not
              replace, human review to create property descriptions for
              marketing purposes. Due to the rapidly advancing and probabilistic
              nature of AI, this tool's output may be incomplete, inaccurate,
              unlawful, or otherwise unsuitable for use.
            </DialogContentText>

            <DialogContentText>
              By utilizing this tool, you acknowledge and agree:
            </DialogContentText>

            <DialogContentText sx={{ display: 'list-item', ml: 3 }}>
              You will conduct a human review of the output prior to its use.
            </DialogContentText>

            <DialogContentText sx={{ display: 'list-item', ml: 3 }}>
              You bear sole responsibility for the accuracy and suitability of
              the output for your use.
            </DialogContentText>

            <DialogContentText sx={{ display: 'list-item', ml: 3 }}>
              You will not allow this tool or its output to be used in any way
              that is unlawful, infringing, defamatory, misleading, or infringes
              on another's privacy rights or confidentiality.
            </DialogContentText>
          </Box>
        )}

        {showActions && (
          <>
            <DialogContentText sx={{ fontWeight: 'bold', mt: 3 }}>
              {acceptCopy}
            </DialogContentText>

            <DialogActions sx={{ justifyContent: 'start', pl: 0, mt: 2 }}>
              <Button
                variant="contained"
                onClick={onAccept}
                className="bmls_btn"
              >
                Accept
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={onDecline}
                className="bmls_btn"
              >
                Decline
              </Button>
            </DialogActions>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
